/**
 * BRAIN/34 P3: helpers for a number's opening hours in the panel (pure; tested). Day names, week start and dates come
 * from Intl in the panel language — no day-name table, no weekend rule.
 */
export type Win = { start: string; end: string };
export type Week = Partial<Record<string, Win[]>>;

/** ISO day numbers (1 = Monday) in the order a calendar in this language starts the week. */
export const weekOrder = (lang: string): number[] => {
  let first = 1;
  try {
    const loc = new Intl.Locale(lang) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    first = loc.getWeekInfo?.().firstDay ?? loc.weekInfo?.firstDay ?? 1;
  } catch {
    first = 1;
  }
  return Array.from({ length: 7 }, (_, i) => ((first - 1 + i) % 7) + 1);
};

/** The name of an ISO day in the language (2026-10-12 was a Monday). */
export const dayName = (lang: string, isoDay: number, style: 'long' | 'short' = 'short'): string =>
  new Intl.DateTimeFormat(lang, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 11 + isoDay)));

const same = (a: Win[] | undefined, b: Win[] | undefined): boolean => JSON.stringify(a ?? []) === JSON.stringify(b ?? []);

/** "Mon–Fri 09:00–12:30, 13:30–18:00 · Sat 10:00–14:00": consecutive days (in week order) with the same hours grouped. */
export const groupDays = (week: Week, lang: string): Array<{ days: string; hours: string }> => {
  const order = weekOrder(lang);
  const out: Array<{ days: string; hours: string }> = [];
  let i = 0;
  while (i < order.length) {
    const d = order[i]!;
    const ws = week[String(d)] ?? [];
    let j = i;
    while (j + 1 < order.length && same(week[String(order[j + 1]!)], ws)) j += 1;
    if (ws.length > 0) {
      const from = dayName(lang, d);
      out.push({ days: j > i ? `${from}–${dayName(lang, order[j]!)}` : from, hours: ws.map((w) => `${w.start}–${w.end}`).join(', ') });
    }
    i = j + 1;
  }
  return out;
};

/** The agent's calling hours (ISO days, one window) as a number's week. */
export const fromAgentHours = (h: { days: number[]; start: string; end: string }): Week =>
  Object.fromEntries(h.days.filter((d) => d >= 1 && d <= 7).map((d) => [String(d), [{ start: h.start, end: h.end }]]));
